import type React from 'react';
import Input, { INPUT_SIZE } from '../../../form/Input';
import Select from '../../../form/Select';
import Pagination from '../../../table/Pagination';
import type { UseOrderAndPaginationControlReturn } from '../../../table/hooks';
import { CreateProjectMenu } from '../components/CreateProjectMenu/CreateProjectMenu';
import { OverviewTabToggle } from '../components/OverviewTabToggle';
import { OVERVIEW_STATUS_FILTER, OVERVIEW_TAB } from '../constants';
import type { OverviewTab } from '../types';

interface OverviewToolbarProps {
    selectedTab: OverviewTab;
    onTabChange: (value: OverviewTab) => void;
    isArchivedTab: boolean;
    onArchiveTabChange: (value: boolean) => void;
    searchTerm: string;
    onSearchTermChange: (value: string) => void;
    pageCount: number;
    orderAndPaginationControl: UseOrderAndPaginationControlReturn;
}

export function OverviewToolbar({
    selectedTab,
    onTabChange,
    isArchivedTab,
    onArchiveTabChange,
    searchTerm,
    onSearchTermChange,
    pageCount,
    orderAndPaginationControl,
}: OverviewToolbarProps): React.ReactElement {
    const searchPlaceholder = gettext('OVERVIEW_SEARCH_PLACEHOLDER');
    const searchInputId = 'measuring-point-overview-search';

    return (
        <>
            <div className="mb-3">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-col gap-3 md:max-w-md md:flex-row md:items-center md:justify-start lg:max-w-lg">
                        <OverviewTabToggle
                            value={selectedTab}
                            onChange={onTabChange}
                            options={[
                                {
                                    value: OVERVIEW_TAB.BY_PROJECT,
                                    label: gettext('OVERVIEW_PAGE_TAB_BY_PROJECT_TITLE'),
                                },
                                {
                                    value: OVERVIEW_TAB.STANDALONE,
                                    label: gettext('OVERVIEW_PAGE_TAB_STANDALONE_TITLE'),
                                },
                            ]}
                            ariaLabel={gettext('OVERVIEW_PAGE_TAB_BY_PROJECT_TITLE')}
                        />
                        <div className="flex shrink-0 items-center gap-3">
                            <label
                                htmlFor="measuring-point-overview-status"
                                className="text-sm font-medium text-gray-700"
                            >
                                {gettext('STATUS')}
                            </label>
                            <Select
                                id="measuring-point-overview-status"
                                small
                                className="min-w-40 text-sm"
                                value={
                                    isArchivedTab
                                        ? OVERVIEW_STATUS_FILTER.ARCHIVED
                                        : OVERVIEW_STATUS_FILTER.ACTIVE
                                }
                                onChange={(event) =>
                                    onArchiveTabChange(
                                        event.target.value === OVERVIEW_STATUS_FILTER.ARCHIVED
                                    )
                                }
                                data={[
                                    [OVERVIEW_STATUS_FILTER.ACTIVE, gettext('ACTIVE')],
                                    [
                                        OVERVIEW_STATUS_FILTER.ARCHIVED,
                                        gettext('OVERVIEW_PAGE_TAB_ARCHIVED_TITLE'),
                                    ],
                                ]}
                            />
                        </div>
                    </div>
                    <CreateProjectMenu />
                </div>
            </div>
            <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex w-full flex-col md:max-w-md lg:max-w-lg">
                    <Input
                        id={searchInputId}
                        value={searchTerm}
                        onChange={(event) => onSearchTermChange(event.target.value)}
                        aria-label={searchPlaceholder}
                        placeholder={searchPlaceholder}
                        size={INPUT_SIZE.MEDIUM}
                        className="text-sm/normal"
                    />
                </div>
                {pageCount > 0 && (
                    <div className="flex w-full justify-end md:w-auto">
                        <Pagination orderAndPaginationControl={orderAndPaginationControl} />
                    </div>
                )}
            </div>
        </>
    );
}
